/**
 * SimulationReport - Rapport de simulation avec métriques et problèmes
 *
 * Affiche les résultats de la simulation : métriques globales,
 * problèmes détectés, et recommandations.
 */

import { useState } from "react";
import {
  AlertTriangle,
  CheckCircle,
  Info,
  X,
  Download,
  Clock,
  TrendingDown,
  Target,
  ThumbsUp,
  ThumbsDown,
  Lightbulb,
  Loader2,
  Sparkles,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import type {
  SimulationResult,
  DetectedIssue,
  IssueSeverity,
} from "../../types/simulation";

interface SimulationReportProps {
  /** Résultat de la simulation */
  result: SimulationResult;

  /** Questions du poll (pour afficher les titres) */
  questions?: Array<{ id: string; title: string }>;

  /** Callback fermeture */
  onClose: () => void;

  /** Callback export PDF (Pro) */
  onExportPdf?: () => void;

  /** Utilisateur Pro */
  isPro?: boolean;

  /** ID du poll (pour navigation vers chat IA) */
  pollId?: string;

  /** Applique les suggestions IA au questionnaire (révision via l'agent) */
  onApplyRevision?: (suggestions: string[]) => Promise<void> | void;

  /** Indique qu'une révision IA est en cours (côté parent) */
  isApplyingRevision?: boolean;

  /** Le résultat affiché provient du cache (pas d'appel IA relancé) */
  fromCache?: boolean;

  /** Horodatage (epoch ms) de mise en cache, si fromCache */
  cachedAt?: number | null;

  /** Relancer une vraie analyse en ignorant le cache */
  onRerun?: () => Promise<void> | void;
}

const SEVERITY_CONFIG: Record<
  IssueSeverity,
  {
    icon: React.ComponentType<{ className?: string }>;
    color: string;
    bgColor: string;
    borderColor: string;
  }
> = {
  critical: {
    icon: AlertTriangle,
    color: "text-red-300",
    bgColor: "bg-red-900/20",
    borderColor: "border-red-700/50",
  },
  warning: {
    icon: AlertTriangle,
    color: "text-yellow-300",
    bgColor: "bg-yellow-900/20",
    borderColor: "border-yellow-700/50",
  },
  info: {
    icon: Info,
    color: "text-blue-300",
    bgColor: "bg-blue-900/20",
    borderColor: "border-blue-700/50",
  },
};

function IssueCard({
  issue,
  questions,
}: {
  issue: DetectedIssue;
  questions?: Array<{ id: string; title: string }>;
}) {
  const { t } = useTranslation("forms");
  const config = SEVERITY_CONFIG[issue.severity];
  const Icon = config.icon;

  // Trouver le titre et le numéro de la question concernée
  const questionIndex =
    issue.questionId && questions
      ? questions.findIndex((q) => q.id === issue.questionId)
      : -1;
  const questionTitle =
    questionIndex >= 0 && questions ? questions[questionIndex].title : null;

  return (
    <div
      className={`${config.bgColor} border ${config.borderColor} rounded-lg p-4`}
    >
      <div className="flex items-start gap-3">
        <Icon className={`w-5 h-5 ${config.color} flex-shrink-0 mt-0.5`} />
        <div className="flex-1">
          <h4 className={`font-semibold ${config.color} mb-1`}>
            {issue.title}
          </h4>
          {questionTitle && (
            <p className="text-xs font-medium text-gray-400 mb-2">
              {t("simulation.report.questionLabel", {
                n: questionIndex + 1,
                title: questionTitle,
              })}
            </p>
          )}
          <p className="text-sm text-gray-300 mb-2">{issue.description}</p>
          {issue.recommendations.length > 0 && (
            <p className="text-sm text-blue-300">{issue.recommendations[0]}</p>
          )}
        </div>
      </div>
    </div>
  );
}

export function SimulationReport({
  result,
  questions,
  onClose,
  onExportPdf,
  isPro = false,
  onApplyRevision,
  isApplyingRevision = false,
  fromCache = false,
  cachedAt = null,
  onRerun,
}: SimulationReportProps) {
  const { t } = useTranslation("forms");
  const { metrics, issues, objectiveValidation } = result;
  const [applyingLocal, setApplyingLocal] = useState(false);
  const [rerunning, setRerunning] = useState(false);
  const isApplying = applyingLocal || isApplyingRevision;

  // Caption « méthode de génération » : l'ancien texte « sans IA » était codé
  // en dur et donc faux dès qu'une question texte passait par Gemini (cause de
  // confusion : on croyait la simulation 100 % locale alors que les réponses
  // texte venaient de Gemini). `estimatedCost.geminiCalls` n'est > 0 que si des
  // appels Gemini ont réellement eu lieu (cf. SimulationService.simulate).
  const usedGeminiForText = (result.estimatedCost?.geminiCalls ?? 0) > 0;
  const overviewCaption = fromCache
    ? t("simulation.report.cacheCaption")
    : usedGeminiForText
      ? t("simulation.report.aiSimCaption")
      : t("simulation.report.localSimCaption");

  // Label « il y a X » à partir d'un timestamp epoch (minutes/heures/jours)
  const formatRelative = (ts: number): string => {
    const diffMin = Math.max(0, Math.round((Date.now() - ts) / 60000));
    if (diffMin < 1) return t("simulation.report.cacheJustNow");
    if (diffMin < 60)
      return t("simulation.report.cacheMinutesAgo", { count: diffMin });
    const diffH = Math.round(diffMin / 60);
    if (diffH < 24)
      return t("simulation.report.cacheHoursAgo", { count: diffH });
    const diffD = Math.round(diffH / 24);
    return t("simulation.report.cacheDaysAgo", { count: diffD });
  };

  const handleRerun = async () => {
    if (!onRerun) return;
    setRerunning(true);
    try {
      await onRerun();
    } finally {
      setRerunning(false);
    }
  };

  const handleApplySuggestions = async () => {
    if (!onApplyRevision || !objectiveValidation?.suggestions.length) return;
    setApplyingLocal(true);
    try {
      await onApplyRevision(objectiveValidation.suggestions);
      onClose();
    } finally {
      setApplyingLocal(false);
    }
  };

  const criticalIssues = issues.filter((i) => i.severity === "critical");
  const highIssues = issues.filter((i) => i.severity === "warning");
  const mediumIssues = issues.filter((i) => i.severity === "info");
  const lowIssues: DetectedIssue[] = [];

  const hasIssues = issues.length > 0;
  const hasCriticalIssues = criticalIssues.length > 0;

  // Formater le temps avec secondes si < 1min
  const formatTimeWithSeconds = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.round(seconds % 60);

    if (minutes === 0) {
      return `${remainingSeconds}s`;
    }
    return `${minutes}min`;
  };

  // Handler pour appliquer une suggestion via le chat IA
  // const handleApplySuggestion = (suggestion: string) => {
  //   if (!pollId) {
  //     console.warn("pollId manquant, impossible d'appliquer la suggestion");
  //     return;
  //   }
  //   onClose();
  //   navigate(`/?new=${Date.now()}`, {
  //     state: {
  //       initialMessage: `Améliore mon questionnaire : ${suggestion}`,
  //       pollId: pollId,
  //       context: "simulation-suggestion",
  //     },
  //   });
  // };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-card dark:bg-[#1e1e1e] rounded-lg shadow-xl max-w-2xl w-full my-8 border border-gray-700">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-700">
          <div>
            <h2 className="text-2xl font-bold text-white">
              {t("simulation.report.title")}
            </h2>
            <p className="text-sm text-gray-400 mt-1">
              {t("simulation.report.subtitle", {
                count: result.respondents.length,
              })}{" "}
              • {formatTimeWithSeconds(metrics.avgTotalTime)}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-200 transition-colors"
            data-testid="simulation-report-close"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-6">
          {/* Bandeau cache : résultat resservi sans nouvel appel IA */}
          {fromCache && (
            <div
              className="flex items-center justify-between gap-3 bg-gray-800/60 border border-gray-700 rounded-lg px-4 py-3"
              data-testid="simulation-report-cache-banner"
            >
              <p className="text-sm text-gray-300">
                {t("simulation.report.cachedResult")}
                {cachedAt ? ` · ${formatRelative(cachedAt)}` : ""}
              </p>
              {onRerun && (
                <button
                  onClick={handleRerun}
                  disabled={rerunning}
                  data-testid="simulation-report-rerun"
                  className="flex items-center gap-1.5 text-sm text-purple-300 hover:text-purple-200 disabled:text-gray-500 disabled:cursor-not-allowed whitespace-nowrap"
                >
                  {rerunning ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : null}
                  {t("simulation.report.rerun")}
                </button>
              )}
            </div>
          )}

          {/* Vue d'ensemble */}
          <div>
            <h3 className="text-lg font-semibold text-white mb-1">
              {t("simulation.report.overview")}
            </h3>
            <p className="text-xs text-gray-500 mb-4">{overviewCaption}</p>
            <div className="grid grid-cols-3 gap-4">
              {/* Completion */}
              <div className="bg-green-900/20 border border-green-700/50 rounded-lg p-4">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 bg-green-600/20 rounded-full flex items-center justify-center">
                    <CheckCircle className="w-5 h-5 text-green-400" />
                  </div>
                  <div className="text-2xl font-bold text-green-300">
                    {Math.round(metrics.avgCompletionRate * 100)}%
                  </div>
                </div>
                <p className="text-sm text-green-400">
                  {t("simulation.report.completionRate")}
                </p>
              </div>

              {/* Time */}
              <div className="bg-blue-900/20 border border-blue-700/50 rounded-lg p-4">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 bg-blue-600/20 rounded-full flex items-center justify-center">
                    <Clock className="w-5 h-5 text-blue-400" />
                  </div>
                  <div className="text-2xl font-bold text-blue-300">
                    {formatTimeWithSeconds(metrics.avgTotalTime)}
                  </div>
                </div>
                <p className="text-sm text-blue-400">
                  {t("simulation.report.avgTime")}
                </p>
              </div>

              {/* Dropoff */}
              <div className="bg-orange-900/20 border border-orange-700/50 rounded-lg p-4">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 bg-orange-600/20 rounded-full flex items-center justify-center">
                    <TrendingDown className="w-5 h-5 text-orange-400" />
                  </div>
                  <div className="text-2xl font-bold text-orange-300">
                    {Math.round(metrics.dropoffRate * 100)}%
                  </div>
                </div>
                <p className="text-sm text-orange-400">
                  {t("simulation.report.dropoffRate")}
                </p>
              </div>
            </div>
          </div>

          {/* Validation d'objectif */}
          {objectiveValidation && (
            <div>
              <h3 className="text-lg font-semibold text-white mb-1 flex items-center gap-2">
                <Target className="w-5 h-5 text-purple-400" />
                {t("simulation.report.objectiveValidation")}
                <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-purple-600/30 text-purple-300 border border-purple-600/40">
                  {t("simulation.report.aiBadge")}
                </span>
              </h3>
              <p className="text-xs text-gray-500 mb-4">
                {t("simulation.report.aiAnalysisCaption")}
              </p>
              {objectiveValidation.error ? (
                /* L'appel IA a échoué : état dédié, pas de faux Points forts/faibles */
                <div
                  className="bg-amber-900/20 border border-amber-700/50 rounded-lg p-5 flex items-start gap-3"
                  data-testid="simulation-report-validation-error"
                >
                  <AlertTriangle className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-amber-300">
                      {t("simulation.report.validationUnavailable")}
                    </p>
                    <p className="text-xs text-amber-400/80 mt-1">
                      {t("simulation.report.validationUnavailableHint")}
                    </p>
                  </div>
                </div>
              ) : (
                <div className="bg-purple-900/20 border border-purple-700/50 rounded-lg p-5">
                  {/* Objectif */}
                  <div className="mb-4">
                    <p className="text-sm text-gray-400 mb-1">
                      {t("simulation.report.objectiveDefined")}
                    </p>
                    <p className="text-white font-medium">
                      "{objectiveValidation.objective}"
                    </p>
                  </div>

                  {/* Score */}
                  <div className="mb-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-gray-400">
                        {t("simulation.report.alignmentScore")}
                      </span>
                      <span
                        className={`text-2xl font-bold ${
                          objectiveValidation.alignmentScore >= 70
                            ? "text-green-400"
                            : objectiveValidation.alignmentScore >= 50
                              ? "text-yellow-400"
                              : "text-red-400"
                        }`}
                      >
                        {objectiveValidation.alignmentScore}%
                      </span>
                    </div>
                    <div className="w-full bg-gray-700 rounded-full h-2">
                      <div
                        className={`h-2 rounded-full transition-all ${
                          objectiveValidation.alignmentScore >= 70
                            ? "bg-green-500"
                            : objectiveValidation.alignmentScore >= 50
                              ? "bg-yellow-500"
                              : "bg-red-500"
                        }`}
                        style={{
                          width: `${objectiveValidation.alignmentScore}%`,
                        }}
                      />
                    </div>
                  </div>

                  {/* Points forts */}
                  {objectiveValidation.strengths.length > 0 && (
                    <div className="mb-4">
                      <div className="flex items-center gap-2 mb-2">
                        <ThumbsUp className="w-4 h-4 text-green-400" />
                        <p className="text-sm font-medium text-green-400">
                          {t("simulation.report.strengths")}
                        </p>
                      </div>
                      <ul className="space-y-1">
                        {objectiveValidation.strengths.map((strength, i) => (
                          <li
                            key={i}
                            className="text-sm text-gray-300 flex items-start gap-2"
                          >
                            <span className="text-green-500">✓</span>
                            <span>{strength}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Points faibles */}
                  {objectiveValidation.weaknesses.length > 0 && (
                    <div className="mb-4">
                      <div className="flex items-center gap-2 mb-2">
                        <ThumbsDown className="w-4 h-4 text-red-400" />
                        <p className="text-sm font-medium text-red-400">
                          {t("simulation.report.weaknesses")}
                        </p>
                      </div>
                      <ul className="space-y-1">
                        {objectiveValidation.weaknesses.map((weakness, i) => (
                          <li
                            key={i}
                            className="text-sm text-gray-300 flex items-start gap-2"
                          >
                            <span className="text-red-500">✗</span>
                            <span>{weakness}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Suggestions */}
                  {objectiveValidation.suggestions.length > 0 && (
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <Lightbulb className="w-4 h-4 text-yellow-400" />
                        <p className="text-sm font-medium text-yellow-400">
                          {t("simulation.report.improvementSuggestions")}
                        </p>
                      </div>
                      <ul className="space-y-2">
                        {objectiveValidation.suggestions.map(
                          (suggestion, i) => (
                            <li
                              key={i}
                              className="text-sm text-gray-300 flex items-start gap-2"
                            >
                              <span className="text-yellow-500">💡</span>
                              <span>{suggestion}</span>
                            </li>
                          ),
                        )}
                      </ul>

                      {/* Appliquer les suggestions IA (révision via l'agent) */}
                      {onApplyRevision && (
                        <button
                          onClick={handleApplySuggestions}
                          disabled={isApplying}
                          data-testid="simulation-report-apply-suggestions"
                          className="mt-4 w-full flex items-center justify-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:bg-gray-700 disabled:cursor-not-allowed disabled:text-gray-400"
                        >
                          {isApplying ? (
                            <>
                              <Loader2 className="w-4 h-4 animate-spin" />
                              {t("simulation.report.applying")}
                            </>
                          ) : (
                            <>
                              <Sparkles className="w-4 h-4" />
                              {t("simulation.report.applySuggestions")}
                            </>
                          )}
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Problèmes détectés */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white">
                {t("simulation.report.detectedIssues")}
              </h3>
              {hasIssues && (
                <span
                  className={`px-3 py-1 rounded-full text-xs font-medium ${
                    hasCriticalIssues
                      ? "bg-red-900/30 text-red-300 border border-red-700/50"
                      : "bg-yellow-900/30 text-yellow-300 border border-yellow-700/50"
                  }`}
                >
                  {t("simulation.report.issuesCount", { count: issues.length })}
                </span>
              )}
            </div>

            {!hasIssues ? (
              <div className="bg-green-900/20 border border-green-700/50 rounded-lg p-6 text-center">
                <div className="w-16 h-16 bg-green-600/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-8 h-8 text-green-400" />
                </div>
                <h4 className="text-lg font-semibold text-green-300 mb-2">
                  {t("simulation.report.noIssuesTitle")}
                </h4>
                <p className="text-sm text-green-400">
                  {t("simulation.report.noIssuesText")}
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {/* Critical */}
                {criticalIssues.map((issue) => (
                  <IssueCard
                    key={issue.id}
                    issue={issue}
                    questions={questions}
                  />
                ))}

                {/* High */}
                {highIssues.map((issue) => (
                  <IssueCard
                    key={issue.id}
                    issue={issue}
                    questions={questions}
                  />
                ))}

                {/* Medium */}
                {mediumIssues.map((issue) => (
                  <IssueCard
                    key={issue.id}
                    issue={issue}
                    questions={questions}
                  />
                ))}

                {/* Low */}
                {lowIssues.map((issue) => (
                  <IssueCard
                    key={issue.id}
                    issue={issue}
                    questions={questions}
                  />
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-700 bg-card dark:bg-[#1a1a1a]">
          {isPro && onExportPdf && (
            <button
              onClick={onExportPdf}
              className="px-4 py-2 text-gray-300 hover:bg-gray-800 rounded-lg transition-colors flex items-center gap-2 border border-gray-700"
              data-testid="simulationreport-button"
            >
              <Download className="w-4 h-4" />
              {t("simulation.report.exportPdf")}
            </button>
          )}
          <button
            onClick={onClose}
            className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
            data-testid="simulationreport-fermer"
          >
            {t("common.close")}
          </button>
        </div>
      </div>
    </div>
  );
}
